"use client";

import { formatTimeWithPreferences } from "@workspace/shared-utils";
import type { ProcessedSet } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";

interface TimeNavigationProps {
	sets: ProcessedSet[];
	selectedDay: string;
	sortAscending?: boolean;
	festivalDetails?: {
		schedule_start_hour?: number;
		schedule_end_hour?: number;
		schedule_overnight?: boolean;
	} | null;
	className?: string;
}

export const TimeNavigation = memo(
	({
		sets,
		selectedDay,
		sortAscending = false,
		festivalDetails,
		className,
	}: TimeNavigationProps) => {
		const scrollContainerRef = useRef<HTMLDivElement>(null);
		const [canScrollLeft, setCanScrollLeft] = useState(false);
		const [canScrollRight, setCanScrollRight] = useState(false);

		// Get user preferences for time formatting
		const userPreferences = useUserPreferences();

		// Helper function to get time label from hour
		const getTimeLabel = useCallback(
			(hour: number): string => {
				// Create a date object for the given hour
				const date = new Date();
				date.setHours(hour, 0, 0, 0);
				return formatTimeWithPreferences(date, userPreferences);
			},
			[userPreferences],
		);

		// Helper function to get mobile-friendly time label
		const getMobileTimeLabel = useCallback(
			(hour: number): string => {
				// Create a date object for the given hour
				const date = new Date();
				date.setHours(hour, 0, 0, 0);

				if (userPreferences.time_format === "24h") {
					return formatTimeWithPreferences(date, userPreferences);
				}

				// For 12h format, use abbreviated version
				if (hour === 0) return "12AM";
				if (hour === 12) return "12PM";
				if (hour < 12) return `${hour}AM`;
				return `${hour - 12}PM`;
			},
			[userPreferences],
		);

		// Helper function to get festival time value for sorting
		const getFestivalTimeValue = useCallback(
			(timeStr: string): number => {
				const getHourValue = (timeStr: string): number => {
					if (timeStr === "12:00AM") return 0;
					if (timeStr === "12:00PM") return 12;

					const parts = timeStr.split(":");
					const hourPart = parts[0];
					if (!hourPart) return 0;

					const hour = Number.parseInt(hourPart, 10);
					if (Number.isNaN(hour)) return 0;

					if (timeStr.includes("AM")) {
						return hour;
					}
					return hour + 12;
				};

				const hourValue = getHourValue(timeStr);

				// For overnight festivals, adjust early AM hours to come after PM hours
				if (festivalDetails?.schedule_overnight) {
					if (hourValue >= 0 && hourValue <= 6) {
						// Early AM hours (12AM-6AM)
						return hourValue + 24; // Map to 24-30 range
					}
				}

				return hourValue;
			},
			[festivalDetails],
		);

		// Helper function to get standard time label (matches data processing format)
		const getStandardTimeLabel = useCallback((hour: number): string => {
			if (hour === 0) return "12:00AM";
			if (hour === 12) return "12:00PM";
			if (hour < 12) return `${hour}:00AM`;
			return `${hour - 12}:00PM`;
		}, []);

		// Get unique time labels for the selected day
		const availableTimeLabels = useMemo(() => {
			const timeSet = new Set<string>();

			sets
				.filter((set) => set.day === selectedDay)
				.forEach((set) => {
					const startTime = new Date(set.start_time);
					const hour = startTime.getHours();
					// Use standard time label for consistency with data processing
					const timeLabel = getStandardTimeLabel(hour);
					timeSet.add(timeLabel);
				});

			const timeLabels = Array.from(timeSet);

			// Sort time labels based on festival schedule and sort preference
			return timeLabels.sort((a, b) => {
				const hourA = getFestivalTimeValue(a);
				const hourB = getFestivalTimeValue(b);
				if (sortAscending) {
					return hourA - hourB;
				}
				return hourB - hourA;
			});
		}, [
			sets,
			selectedDay,
			sortAscending,
			getFestivalTimeValue,
			getStandardTimeLabel,
		]);

		// Function to scroll to a specific time section
		const scrollToTimeSection = useCallback((timeLabel: string) => {
			const element = document.getElementById(
				`time-section-${timeLabel.replace(/[\s:.]/g, "-")}`,
			);
			if (element) {
				// Calculate offset to account for sticky header
				// Sticky header includes: main nav (56px) + tab controls (~180px) + padding
				const stickyHeaderOffset = 250; // Adjust this value as needed

				const elementPosition =
					element.getBoundingClientRect().top + window.pageYOffset;
				const offsetPosition = elementPosition - stickyHeaderOffset;

				window.scrollTo({
					top: offsetPosition,
					behavior: "smooth",
				});
			}
		}, []);

		// Function to check scroll state for blur gradients
		const checkScrollState = useCallback(() => {
			const container = scrollContainerRef.current;
			if (!container) return;

			const { scrollLeft, scrollWidth, clientWidth } = container;
			setCanScrollLeft(scrollLeft > 0);
			setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
		}, []);

		// Set up scroll listeners and initial check
		useEffect(() => {
			const container = scrollContainerRef.current;
			if (!container) return;

			// Initial check
			checkScrollState();

			// Add scroll listener
			container.addEventListener("scroll", checkScrollState);

			// Add resize listener to recheck on window resize
			window.addEventListener("resize", checkScrollState);

			return () => {
				container.removeEventListener("scroll", checkScrollState);
				window.removeEventListener("resize", checkScrollState);
			};
		}, [checkScrollState]);

		// Don't render if no times available
		if (availableTimeLabels.length === 0) {
			return null;
		}

		return (
			<div className={cn("relative w-full", className)}>
				{/* Left blur gradient */}
				{canScrollLeft && (
					<div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent" />
				)}

				{/* Right blur gradient */}
				{canScrollRight && (
					<div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-8 bg-gradient-to-l from-background to-transparent" />
				)}

				{/* Scrollable container */}
				<div
					ref={scrollContainerRef}
					className="scrollbar-none flex items-center gap-2 overflow-x-auto"
					style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
				>
					{availableTimeLabels.map((timeLabel) => {
						// Extract hour from timeLabel to get formatted versions
						const hour =
							timeLabel === "12:00AM"
								? 0
								: timeLabel === "12:00PM"
									? 12
									: timeLabel.includes("AM")
										? Number.parseInt(timeLabel.split(":")[0] || "0")
										: Number.parseInt(timeLabel.split(":")[0] || "0") + 12;

						// Get display labels based on user preferences
						const displayLabel = getTimeLabel(hour);
						const mobileLabel = getMobileTimeLabel(hour);

						return (
							<Button
								key={timeLabel}
								variant="secondary"
								size="sm"
								onClick={() => scrollToTimeSection(timeLabel)}
								className="flex-shrink-0 whitespace-nowrap px-4 h-10 font-medium text-xs active:scale-[0.99] rounded-xl"
							>
								<span className="sm:hidden">{mobileLabel}</span>
								<span className="hidden sm:inline">{displayLabel}</span>
							</Button>
						);
					})}
				</div>
			</div>
		);
	},
);

TimeNavigation.displayName = "TimeNavigation";
